import React, { type ReactNode, useState } from 'react';

import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import Portal from '@atlaskit/portal/portal';

import { SidePanelSurface } from '../../common/ui/side-panel-surface/SidePanelSurface';
import { useUIConfig } from '../../common/utils/ui-config/useUIConfig';
import { ConversationAssistantUIModes } from '../../types';
import { SessionContextLayout } from '../session-right-panel/SessionContextLayout';
import { ChatPanelContext } from './ChatPanelContext';

export type MainWrapperProps = {
	testId?: string;
	isNav4Enabled?: boolean;
	children: ReactNode;
	mode?: ConversationAssistantUIModes;
};

export const MainWrapper = ({
	testId,
	children,
	isNav4Enabled,
	mode,
}: MainWrapperProps): JSX.Element => {
	const isCodeDiffEnabled = expVal('rovo_a2ui_code_diff_layout', 'isCodeDiffEnabled', false);
	const [chatPanel, setChatPanel] = useState<HTMLDivElement | null>(null);
	const isMiniModal = mode === ConversationAssistantUIModes.MINI_MODAL;

	const { isContextPanelEnabled } = useUIConfig();

	const renderInnerChildren = (): ReactNode => {
		if (!isContextPanelEnabled || !fg('agent_session_experience_fg')) {
			return children;
		}

		return <SessionContextLayout isMiniModal={isMiniModal}>{children}</SessionContextLayout>;
	};

	// Purely for FE team testing fullscreen where branch deploys are accessible
	// The use of localStorage and a private FF should make this safe, and it's our
	// best bet to improve dev velocity at the moment. Once Atlas has local consumption
	// we can remove this
	const fullscreenDebugEnabled =
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		localStorage.getItem('DEBUG_rovo_chat_fullscreen') === 'true' &&
		fg('rovo_chat_show_local_frontend_components');

	const shouldUseWrapperNav4Styles = isNav4Enabled;

	if (fullscreenDebugEnabled) {
		return (
			<Portal zIndex={500}>
				<SidePanelSurface
					testId={testId}
					isNav4Enabled={shouldUseWrapperNav4Styles}
					isMiniModal={isMiniModal}
					isFullscreenDebug
					isChatPanel
					surfaceRef={isCodeDiffEnabled ? setChatPanel : undefined}
				>
					{isCodeDiffEnabled ? (
						<ChatPanelContext.Provider value={chatPanel}>
							{renderInnerChildren()}
						</ChatPanelContext.Provider>
					) : (
						renderInnerChildren()
					)}
				</SidePanelSurface>
			</Portal>
		);
	}

	return (
		<SidePanelSurface
			testId={testId}
			isNav4Enabled={shouldUseWrapperNav4Styles}
			isMiniModal={isMiniModal}
			isChatPanel
			surfaceRef={isCodeDiffEnabled ? setChatPanel : undefined}
		>
			{isCodeDiffEnabled ? (
				<ChatPanelContext.Provider value={chatPanel}>
					{renderInnerChildren()}
				</ChatPanelContext.Provider>
			) : (
				renderInnerChildren()
			)}
		</SidePanelSurface>
	);
};
